import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Skeleton, Stack, Typography as T, Tooltip } from "@mui/material";
import CreateDataset from "datasets/components/CreateDataset";
import useDatasets from "datasets/hooks/useDatasets";
import { PlusIcon } from "~/assets/icons";
import CardGrid from "~/components/CardGrid";
import DataBrowserTile from "~/components/DataBrowserTile";
import DatasetList from "~/components/DatasetList";
import useDataBrowserResources from "~/hooks/useDataBrowserResources";
import useUserPermission from "~/hooks/useUserPermission";

const Loading = () => (
  <>
    {Array.from({ length: 4 }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={200} />
    ))}
  </>
);

const DatasetLoading = () => (
  <>
    {Array.from({ length: 4 }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={200} />
    ))}
  </>
);

const DatasetsSection = () => {
  const limit = 4;
  // TODO: Handle limit when implementing pagination from backend
  const { data: datasets, isLoading, error } = useDatasets({ sortBy: "updatedAt" });
  const hasCreatePermission = useUserPermission("create:catalog-dataset");
  const isEmpty = !isLoading && (!datasets || datasets.length === 0);

  if (error) {
    return null;
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <T component="h3" variant="body1" fontWeight={600}>
          Datasets
        </T>

        {!isEmpty && (
          <Box display="flex" alignItems="center" gap={1}>
            {hasCreatePermission && (
              <CreateDataset
                callToAction={
                  <Tooltip arrow enterDelay={500} title="Create a dataset" placement="top">
                    <Button
                      aria-label="Create dataset"
                      variant="outlined"
                      size="small"
                      sx={{ aspectRatio: 1, borderRadius: 2, height: 38 }}
                    >
                      <PlusIcon />
                    </Button>
                  </Tooltip>
                }
              />
            )}
            {Number(datasets?.length) > limit && (
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
                component={RouterLink}
                to="/data-browser/datasets"
              >
                View All
              </Button>
            )}
          </Box>
        )}
      </Box>
      <DatasetList showEmpty={isEmpty} size="small" limit={limit} />
    </Box>
  );
};

export const DataBrowserGrid = () => {
  const { isLoading: datasetsLoading, error: datasetsError } = useDatasets();
  const {
    resources,
    loading: dataBrowserLoading,
    error: dataBrowserError,
  } = useDataBrowserResources();

  if (datasetsError || dataBrowserError) {
    return (
      <Alert severity="error" variant="outlined">
        Unable to load data
      </Alert>
    );
  }

  return (
    <Stack gap={4}>
      {datasetsLoading && dataBrowserLoading ? (
        <CardGrid>
          <Loading />
        </CardGrid>
      ) : (
        <>
          <DatasetsSection />
          {resources.length > 0 && (
            <Stack gap={2}>
              <T component="h3" variant="body1" fontWeight={600}>
                Additional Resources
              </T>
              <CardGrid>
                {resources.map((props) => (
                  <DataBrowserTile key={props.label} {...props} />
                ))}
              </CardGrid>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default DataBrowserGrid;
